"use client";

/**
 * Hook for managing notifications with automatic polling.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  notificationsGetNotifications,
  notificationsGetUnreadCount,
  notificationsMarkAsRead,
  notificationsMarkAllRead,
} from "../sdk";
import type {
  NotificationResponse,
  NotificationType,
  NotificationStatus,
} from "../sdk";

// Re-export types for consumers
export type { NotificationResponse, NotificationType, NotificationStatus };

interface UseNotificationsOptions {
  pollInterval?: number; // Default: 30000ms (30 seconds)
  enabled?: boolean;
}

interface UseNotificationsResult {
  notifications: NotificationResponse[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

export function useNotifications(
  options: UseNotificationsOptions = {}
): UseNotificationsResult {
  const { pollInterval = 30000, enabled = true } = options;

  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!enabled) return;

    try {
      setIsLoading(true);

      const [notificationsResponse, unreadResponse] = await Promise.all([
        notificationsGetNotifications(),
        notificationsGetUnreadCount(),
      ]);

      if (!isMounted.current) return;

      if (notificationsResponse.error) {
        throw new Error(
          (notificationsResponse.error as { detail?: string }).detail ||
            "Failed to fetch notifications"
        );
      }

      if (unreadResponse.error) {
        throw new Error(
          (unreadResponse.error as { detail?: string }).detail ||
            "Failed to fetch unread count"
        );
      }

      setNotifications(notificationsResponse.data?.notifications || []);
      setUnreadCount(unreadResponse.data?.count || 0);
      setError(null);
    } catch (err) {
      if (isMounted.current) {
        setError((err as Error).message);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [enabled]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await notificationsMarkAsRead({
        path: { notification_id: notificationId },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            "Failed to mark notification as read"
        );
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, status: "read" as NotificationStatus } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationsMarkAllRead();

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            "Failed to mark all notifications as read"
        );
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, status: "read" as NotificationStatus }))
      );
      setUnreadCount(0);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    isMounted.current = true;

    if (!enabled) return;

    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, pollInterval);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [enabled, pollInterval, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
}
